import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ProjectService } from '../project.service';
import { DataService } from '../../network/data.service';
import { ViewsModel } from './views-model';
import { MainFcstWalkModel } from './fcst-walk-model';
import { CardHelper } from '../../../cards/cardHelper';

@Injectable({
  providedIn: 'root'
})
export class CardsLoadingService {
  private _viewsModel = new BehaviorSubject<ViewsModel | null>(null);
  private _mainFcstWalkModel = new BehaviorSubject<MainFcstWalkModel | null>(null);
  private _loading = new BehaviorSubject<boolean>(false);
  
  // Track ongoing requests to prevent duplicates
  private _loadViewsPromise: Promise<ViewsModel> | null = null;
  private _loadMainFcstWalkPromise: Promise<MainFcstWalkModel | null> | null = null;
  private _currentProjectId: string | null = null;
  
  // Project history for debugging
  private _projectHistory: { projectId: string; timestamp: Date; action: string }[] = [];
  
  // Route whitelist - routes where cached data should be preserved
  private _routeWhitelist: string[] = ['cards', 'project-overview', 'confidence'];
  
  public readonly viewsModel$ = this._viewsModel.asObservable();
  public readonly mainFcstWalkModel$ = this._mainFcstWalkModel.asObservable();
  public readonly loading$ = this._loading.asObservable();

  constructor(
    private projectService: ProjectService,
    private dataService: DataService,
    private router: Router
  ) {
    // Subscribe to router events to monitor navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this._handleRouteChange(event.url);
    });
  }

  get viewsModel(): ViewsModel | null {
    return this._viewsModel.value;
  }

  get mainFcstWalkModel(): MainFcstWalkModel | null {
    return this._mainFcstWalkModel.value;
  }

  get loading(): boolean {
    return this._loading.value;
  }

  async loadViews(projectId: string, forceReload: boolean = false): Promise<ViewsModel> {
    if (this._currentProjectId !== projectId) {
      if (this._currentProjectId) {
        this._addProjectHistoryEntry(this._currentProjectId, 'wiped');
      }
      this._wipeProjectData();
      this._currentProjectId = projectId;
      this._addProjectHistoryEntry(projectId, 'switched_to');
    }
    
    if (!forceReload && this._viewsModel.value) {
      this._addProjectHistoryEntry(projectId, 'cached_access');
      return this._viewsModel.value;
    }
    
    if (!forceReload && this._loadViewsPromise) {
      this._addProjectHistoryEntry(projectId, 'reused_request');
      return this._loadViewsPromise;
    }
    
    this._addProjectHistoryEntry(projectId, 'loading_started');
    this._loading.next(true);
    if (forceReload) {
      this._viewsModel.next(null);
    }
    
    this._loadViewsPromise = this._performLoadViews(projectId);
    
    try {
      const result = await this._loadViewsPromise;
      this._addProjectHistoryEntry(projectId, 'loading_completed');
      return result;
    } catch (error) {
      this._addProjectHistoryEntry(projectId, 'loading_failed');
      throw error;
    } finally {
      this._loadViewsPromise = null;
      this._loading.next(false);
    }
  }

  private async _performLoadViews(projectId: string): Promise<ViewsModel> {
    try {
      const viewsModel = await this.projectService.getViews(projectId);
      this._viewsModel.next(viewsModel);
      
      // If forecast adjustments are done, load the main forecast walk model
      if (viewsModel.haveForecastAdjustmentsDone) {
        try {
          await this.loadMainFcstWalkModel(projectId);
        } catch (error) {
          // Don't fail the entire operation if forecast walk model fails
        }
      }
      
      return viewsModel;
    } catch (error) {
      throw error;
    }
  }

  async loadMainFcstWalkModel(projectId: string, forceReload: boolean = false): Promise<MainFcstWalkModel | null> {
    if (this._currentProjectId !== projectId) {
      if (this._currentProjectId) {
        this._addProjectHistoryEntry(this._currentProjectId, 'wiped');
      }
      this._wipeProjectData();
      this._currentProjectId = projectId;
      this._addProjectHistoryEntry(projectId, 'switched_to');
    }
    
    if (!forceReload && this._mainFcstWalkModel.value) {
      this._addProjectHistoryEntry(projectId, 'cached_fcst_access');
      return this._mainFcstWalkModel.value;
    }
    
    if (!forceReload && this._loadMainFcstWalkPromise) {
      this._addProjectHistoryEntry(projectId, 'reused_fcst_request');
      return this._loadMainFcstWalkPromise;
    }
    
    this._addProjectHistoryEntry(projectId, 'fcst_loading_started');
    if (forceReload) {
      this._mainFcstWalkModel.next(null);
    }
    this._loadMainFcstWalkPromise = this._performLoadMainFcstWalkModel(projectId);
    
    try {
      const result = await this._loadMainFcstWalkPromise;
      this._addProjectHistoryEntry(projectId, result ? 'fcst_loading_completed' : 'fcst_no_data');
      return result;
    } catch (error) {
      this._addProjectHistoryEntry(projectId, 'fcst_loading_failed');
      throw error;
    } finally {
      this._loadMainFcstWalkPromise = null;
    }
  }

  private async _performLoadMainFcstWalkModel(projectId: string): Promise<MainFcstWalkModel | null> {
    try {
      const mainFcstWalkModel = await this.dataService.get<MainFcstWalkModel>('processing/GetMainFcstWalk/' + projectId);
      
      if (mainFcstWalkModel != null) {
        CardHelper.initMainFcstWalkModel(mainFcstWalkModel);
        this._mainFcstWalkModel.next(mainFcstWalkModel);
        return mainFcstWalkModel;
      }
      
      return null;
    } catch (error) {
      throw error;
    }
  }

  updateViewsModel(viewsModel: ViewsModel): void {
    this._viewsModel.next(viewsModel);
    if (this._currentProjectId) {
      this._addProjectHistoryEntry(this._currentProjectId, 'views_updated');
    }
  }

  updateMainFcstWalkModel(mainFcstWalkModel: MainFcstWalkModel): void {
    this._mainFcstWalkModel.next(mainFcstWalkModel);
    if (this._currentProjectId) {
      this._addProjectHistoryEntry(this._currentProjectId, 'fcst_updated');
    }
  }

  clearData(): void {
    if (this._currentProjectId) {
      this._addProjectHistoryEntry(this._currentProjectId, 'data_cleared');
    }
    
    this._viewsModel.next(null);
    this._mainFcstWalkModel.next(null);
    this._loading.next(false);
    this._loadViewsPromise = null;
    this._loadMainFcstWalkPromise = null;
    this._currentProjectId = null;
  }

  setLoading(loading: boolean): void {
    this._loading.next(loading);
  }

  hasDataForProject(projectId: string): boolean {
    return this._currentProjectId === projectId && this._viewsModel.value !== null;
  }

  hasMainFcstWalkDataForProject(projectId: string): boolean {
    return this._currentProjectId === projectId && this._mainFcstWalkModel.value !== null;
  }

  get currentProjectId(): string | null {
    return this._currentProjectId;
  }

  get projectHistory(): { projectId: string; timestamp: Date; action: string }[] {
    return [...this._projectHistory]; // Return a copy to prevent external modifications
  }

  clearProjectHistory(): void {
    this._projectHistory = [];
  }

  private _addProjectHistoryEntry(projectId: string, action: string): void {
    const entry = {
      projectId,
      action,
      timestamp: new Date()
    };
    
    this._projectHistory.push(entry);
    
    if (this._projectHistory.length > 50) {
      this._projectHistory = this._projectHistory.slice(-50);
    }
  }

  private _wipeProjectData(): void {
    this._viewsModel.next(null);
    this._mainFcstWalkModel.next(null);
    this._loading.next(false);
    this._loadViewsPromise = null;
    this._loadMainFcstWalkPromise = null;
  }

  /**
   * Handle route changes and invalidate data if navigating to non-whitelisted routes
   */
  private _handleRouteChange(url: string): void {
    // Extract the route path from the URL (remove query parameters and fragments)
    const routePath = url.split('?')[0].split('#')[0].replace('/', '');
    
    // Check if the current route is in the whitelist
    const isWhitelistedRoute = this._routeWhitelist.some(whitelistedRoute => 
      routePath === whitelistedRoute || routePath.startsWith(whitelistedRoute + '/')
    );
    
    if (!isWhitelistedRoute && this._currentProjectId) {
      this._addProjectHistoryEntry(this._currentProjectId, `data_cleared_route_change_to_${routePath}`);
      this.clearData();
    }
  }

  /**
   * Get the current route whitelist
   */
  getRouteWhitelist(): string[] {
    return [...this._routeWhitelist];
  }

  /**
   * Set the route whitelist - routes where cached data should be preserved
   */
  setRouteWhitelist(routes: string[]): void {
    this._routeWhitelist = routes;
    this._addProjectHistoryEntry(this._currentProjectId || 'unknown', `whitelist_updated_to_${routes.join(',')}`);
  }

  /**
   * Add a route to the whitelist
   */
  addToWhitelist(route: string): void {
    if (!this._routeWhitelist.includes(route)) {
      this._routeWhitelist.push(route);
      this._addProjectHistoryEntry(this._currentProjectId || 'unknown', `whitelist_added_${route}`);
    }
  }

  /**
   * Remove a route from the whitelist
   */
  removeFromWhitelist(route: string): void {
    const index = this._routeWhitelist.indexOf(route);
    if (index > -1) {
      this._routeWhitelist.splice(index, 1);
      this._addProjectHistoryEntry(this._currentProjectId || 'unknown', `whitelist_removed_${route}`);
    }
  }

  /**
   * Manually invalidate cached data - useful when you know data has been updated elsewhere
   */
  invalidateCache(reason?: string): void {
    if (this._currentProjectId) {
      this._addProjectHistoryEntry(this._currentProjectId, `manual_invalidation_${reason || 'no_reason'}`);
    }
    this.clearData();
  }

  /**
   * Check if data should be preserved for the current route
   */
  isCurrentRouteWhitelisted(): boolean {
    const currentUrl = this.router.url;
    const routePath = currentUrl.split('?')[0].split('#')[0].replace('/', '');
    
    return this._routeWhitelist.some(whitelistedRoute => 
      routePath === whitelistedRoute || routePath.startsWith(whitelistedRoute + '/')
    );
  }
}
